import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { BtnV2, Icon, l, Toggle } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import sharedCm from 'apps/aiAutomation/components/EngagementGoalEditor/components/EditorLayout/styles.module.css'
import {
  SubmitRef,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { BotAssigneePicker } from 'common/settings/components/BotAssigneePicker'
import { HookFormTextArea } from 'components/HookFormControls/HookFormTextArea/HookFormTextArea'

import { EmptyAssignee } from '../../lib/constants'
import { ConfigFormValues } from '../../lib/types'
import { configFormSchema } from '../../lib/validation'

import { FieldsEditor } from './components/FieldsEditor'

import cm from './styles.module.css'

interface ConfigEditorProps {
  isActive: boolean
  isLoading: boolean
  configData: ConfigFormValues | null

  onFinish: (data: ConfigFormValues) => void
}

export const ConfigEditor = forwardRef<SubmitRef<ConfigFormValues>, ConfigEditorProps>(
  ({ isLoading, onFinish, isActive, configData }, ref) => {
    const dispatch = useAppDispatch()

    const form = useForm<ConfigFormValues>({
      defaultValues: {
        context: configData?.context ?? '',
        fields: configData?.fields ?? [],
        assignee: configData?.assignee ?? EmptyAssignee,
        useWaId: configData?.useWaId ?? false,
      },
      resolver: zodResolver(configFormSchema),
    })

    const { control, setValue, handleSubmit, formState } = form

    const assignee = useWatch({
      control: control,
      name: 'assignee',
    })

    const [isAssignmentEnabled, setAssignmentEnabled] = useState(
      configData ? Boolean(assignee.user_id || assignee.group_id) : false,
    )

    const onAssigneeToggle = useCallback(() => {
      if (isAssignmentEnabled) {
        setValue('assignee', EmptyAssignee)
      }

      setAssignmentEnabled(!isAssignmentEnabled)
    }, [isAssignmentEnabled, setValue])

    useEffect(() => {
      if (isActive) {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: formState.isDirty }))
      } else {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
      }
    }, [dispatch, formState.isDirty, isActive])

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          let result: SubmitResult<ConfigFormValues> = { isValid: false }

          await handleSubmit((data) => (result = { isValid: true, data }))()
          return result
        },
      }),
      [handleSubmit],
    )

    return (
      <section>
        <div className={sharedCm.section}>
          <h4 className={cx('font-medium', !isActive && 'text-secondary')}>
            {l.translate('Choose the info you want AI to collect')}
          </h4>

          <p className="m-t-xxs m-b text-secondary">
            {l.translate('AI will ask for each item one at a time in a natural conversation.')}
          </p>

          <FormProvider {...form}>
            <FieldsEditor isActive={isActive} />

            <HookFormTextArea
              control={control}
              disabled={!isActive}
              name="context"
              label={l.translate('Why are you collecting this info?')}
              helpText={l.translate('This helps AI explain the reason for asking')}
              placeholder={l.translate(
                'Explain briefly how you’ll use this info and why it matters to your customer.',
              )}
              className="m-t-lg"
              minRows={6}
              maxRows={14}
            />

            <h4 className="m-t-lg m-b-sm font-medium">
              {l.translate('If the person doesn’t want to share…')}
            </h4>
          </FormProvider>

          <div className={cm.assigneeToggle}>
            <Icon.Assign className="flex-shrink-0" />

            <Toggle
              className="flex-grow-1"
              checked={isAssignmentEnabled}
              onChange={onAssigneeToggle}
              label={l.translate('Assigns to a person')}
              orientation="left"
              disabled={!isActive}
            />

            {isAssignmentEnabled && (
              <div className="m-t-sm flex-grow-1">
                <BotAssigneePicker
                  disabled={!isActive}
                  value={assignee.user_id || assignee.group_id}
                  onChange={(event) => setValue('assignee', event.target)}
                />
              </div>
            )}
          </div>
        </div>

        {isActive && !configData && (
          <div className="d-flex justify-end m-b-lg">
            <BtnV2 loading={isLoading} onClick={handleSubmit(onFinish)} className="m-t-sm">
              {l.translate('Next')}
            </BtnV2>
          </div>
        )}
      </section>
    )
  },
)
